import React, { useCallback, useEffect, useState } from 'react';
import { Stamp, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const Party = ({ label, ok, name, at, testId }) => (
  <div className={`rounded-xl border p-3 ${ok ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`} data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-sm font-semibold ${ok ? 'text-green-700' : 'text-gray-500'}`}>{ok ? `Signé${name ? ` · ${name}` : ''}` : 'En attente'}</p>
    {ok && at && <p className="text-[11px] text-gray-500">le {fmt(at)}</p>}
  </div>
);

export const SignatureStatusBadge = ({ status }) => {
  if (!status) return null;
  if (status.fully_signed) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700" data-testid="sign-status-full"><CheckCircle2 className="w-3 h-3" />Entièrement signé</span>;
  }
  const n = Number(status.agency_signed) + Number(status.tenant_signed);
  return <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-testid="sign-status-partial">{n}/2 signatures</span>;
};

export const AgencySignButton = ({ docType, docId }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!docId) return;
    api.get(`/agency-signatures/${docType}/${docId}`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [docType, docId]);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/agency-signatures/${docType}/${docId}`);
      setStatus(r.data);
      toast.success(r.data.fully_signed ? 'Document entièrement signé' : "Signature de l'agence apposée");
    } catch (e) { toast.error(e?.response?.data?.detail || 'Signature impossible'); } finally { setBusy(false); }
  };

  if (!status) return null;
  const tid = `${docType}-${docId}`;
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)} title="Signature de l'agence"
        className={`relative ${status.agency_signed ? 'text-green-600' : 'text-[#B85C38]'} hover:bg-[#F5EFE1]`} data-testid={`agency-sign-btn-${tid}`}>
        <Stamp className="w-4 h-4" />
        {status.fully_signed && <CheckCircle2 className="absolute -top-0.5 -right-0.5 w-3 h-3 text-green-600 bg-white rounded-full" />}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="agency-sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#2B3A2C] flex items-center gap-2">Signatures <SignatureStatusBadge status={status} /></DialogTitle>
            <DialogDescription>{docType === 'lease' ? 'Contrat de bail' : 'État des lieux'} — l'agence et le locataire doivent signer.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Party label="Agence" ok={status.agency_signed} name={status.agency_signer_name} at={status.agency_signed_at} testId="agency-sign-party-agency" />
            <Party label="Locataire" ok={status.tenant_signed} name={status.tenant_signer_name} at={status.tenant_signed_at} testId="agency-sign-party-tenant" />
          </div>
          {!status.agency_signed && (status.has_saved_signature ? (
            <Button onClick={sign} disabled={busy} className="w-full rounded-xl bg-[#B85C38] hover:bg-[#9a4a2c] text-white" data-testid="agency-sign-apply">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Stamp className="w-4 h-4 mr-2" />}Apposer la signature de l'agence
            </Button>
          ) : (
            <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="agency-sign-missing">
              Aucune signature d'agence enregistrée. <Link to="/agency-settings" className="font-semibold underline">Configurez-la dans les paramètres</Link>.
            </p>
          ))}
          {!status.tenant_signed && <p className="text-xs text-gray-500">Le locataire peut signer via le lien de signature à distance.</p>}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AgencySignButton;
